import { PropsWithChildren, createContext, useContext, useEffect, useRef, useState } from 'react';
import { ISharedDialogData, ISharedSnackbarData, IUserNotificationsContext, } from './user-notifications.model';
import { InfoDialog } from '../../components/dialogs/info-dialog';
import { InfoSnackbar } from '../../components/dialogs/info-snackbar';
import { ThemeProvider } from '@emotion/react';
import { CustomThemeContext } from '../theme/custom-theme-context';

export const UserNotificationsContext = createContext<IUserNotificationsContext>({} as IUserNotificationsContext);

const DEFAULT_DIALOG_DETAILS: ISharedDialogData = {
  active: false,
  title: '',
  message: '',
  buttonCopy: '',
  cancelButtonCopy: '',
  hideCancelButton: false,
  have_form_input: false,
  form_value: ''
};

export const UserNotificationsProvider = ({ children }: PropsWithChildren) => {
  const customThemeContext = useContext(CustomThemeContext);
  const [theme, setTheme] = useState(customThemeContext.theme);

  const [dialogDetails, setDialogDetails] = useState<ISharedDialogData>({ ...DEFAULT_DIALOG_DETAILS });

  const [snackbarDetails, setSnackbarDetails] = useState<ISharedSnackbarData>({
    open: false,
    transition: undefined,
    message: '',
    duration: 5000,
    vertical: 'bottom',
    horizontal: 'left',
    backgroundColor: 'primary.dark',
  });

  const [resolveFunction, setResolveFunction] = useState<any>();

  // The id of the delayed cleanup scheduled by onUserResponse, so a dialog opened inside
  // that 250ms window can cancel it. Without this, the stale timer closes the new dialog
  // without ever resolving its promise, leaving the caller's await hanging forever.
  const resetTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  async function openDialog(inputDetails: ISharedDialogData) {
    if (resetTimeoutRef.current) {
      clearTimeout(resetTimeoutRef.current);
      resetTimeoutRef.current = undefined;
    }
    // Spread the pristine defaults, not the previous dialog's details: with the reset
    // timer cancellable, merging over the old state would let a dialog opened inside
    // that window inherit stale fields (e.g. an Ok-only hideCancelButton) for its whole
    // life. Every field a dialog needs must come from its own inputDetails.
    setDialogDetails({ ...DEFAULT_DIALOG_DETAILS, ...inputDetails })
    var promise = new Promise((resolve) => { setResolveFunction(() => resolve) })

    return await promise
      .then((response) => {
        return response
      })
      .catch(err => {
        console.log(err)
      })
  }

  function onUserResponse(value: boolean, formValue: string) {
    if (formValue) {
      resolveFunction({ value: value, form_value: formValue });
    } else {
      resolveFunction(value);
    }

    // Cleanup
    setResolveFunction(undefined);
    setDialogDetails({ ...dialogDetails, active: false });
    resetTimeoutRef.current = setTimeout(() => {
      resetTimeoutRef.current = undefined;
      setDialogDetails({ ...DEFAULT_DIALOG_DETAILS });
    }, 250);
  };

  function openSnackbar(inputDetails: ISharedSnackbarData) {
    setSnackbarDetails({ ...snackbarDetails, ...inputDetails })
  }

  function closeSnackbar() {
    setSnackbarDetails({ ...snackbarDetails, open: false })
    setTimeout(() => {
      setSnackbarDetails({
        open: false, transition: undefined, message: '', duration: 3000, vertical: 'bottom', horizontal: 'left', backgroundColor: 'primary.dark',
      })
    }, 250);
  }


  useEffect(() => {
    setTheme(customThemeContext.theme)
  }, [customThemeContext.theme])

  return (
    <ThemeProvider theme={{ ...theme }}>
      <UserNotificationsContext.Provider value={{ onUserResponse, openDialog, openSnackbar, closeSnackbar, snackbarDetails, dialogDetails }}>
        {children}
        <InfoDialog />
        <InfoSnackbar />
      </UserNotificationsContext.Provider>
    </ThemeProvider>
  )
}